import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSupabaseClient } from '../lib/supabase';
import { shouldShowEventInWebList } from '../domain/eventLifecycle';
import type { EventMembershipRow, WebEvent, WebEventAccessRole } from '../types/events';

export const EVENT_COLUMNS = `
  id,
  name,
  description,
  code,
  organizer_id,
  location_name,
  location_address,
  is_active,
  is_public,
  join_type,
  event_date,
  start_time,
  end_time,
  created_at,
  expires_at,
  expire_action,
  photo_remove_policy,
  allow_guest_downloads,
  auto_upload_enabled,
  lifecycle_state,
  lifecycle_state_changed_at,
  lifecycle_state_reason,
  storage_status,
  storage_subscription_active,
  deleted_at,
  archived_at,
  closed_at,
  uploads_locked,
  is_upload_locked,
  cover_photo_url,
  preview_thumbnail_urls
`;

function normalizeMembershipRole(role?: string | null): WebEventAccessRole {
  return role === 'co_admin' ? 'co_admin' : 'member';
}

function eventSortValue(event: WebEvent): number {
  const value = event.start_time || event.event_date || event.created_at;
  const time = value ? new Date(value).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
}

export function mapEventForWeb(
  row: Record<string, unknown>,
  userId: string,
  membershipRole?: string | null,
): WebEvent {
  return {
    ...(row as Omit<WebEvent, 'access_role'>),
    access_role:
      row.organizer_id === userId ? 'organizer' : normalizeMembershipRole(membershipRole),
  } as WebEvent;
}

export async function loadVisibleEventsForUser(userId: string): Promise<WebEvent[]> {
  const client = getSupabaseClient();

  const { data: memberships, error: membershipError } = await client
    .from('event_members')
    .select('event_id, role')
    .eq('user_id', userId);
  if (membershipError) throw membershipError;

  const membershipRows = (memberships ?? []) as EventMembershipRow[];
  const membershipByEvent = new Map(membershipRows.map((row) => [row.event_id, row.role]));
  const memberIds = membershipRows.map((row) => row.event_id).filter(Boolean);

  const organizerPromise = client
    .from('events')
    .select(EVENT_COLUMNS)
    .eq('organizer_id', userId);

  const memberPromise =
    memberIds.length > 0
      ? client.from('events').select(EVENT_COLUMNS).in('id', memberIds)
      : Promise.resolve({ data: [], error: null });

  const [organizerResult, memberResult] = await Promise.all([organizerPromise, memberPromise]);
  if (organizerResult.error) throw organizerResult.error;
  if (memberResult.error) throw memberResult.error;

  const byId = new Map<string, WebEvent>();
  for (const row of [...(organizerResult.data ?? []), ...(memberResult.data ?? [])]) {
    const event = mapEventForWeb(
      row as Record<string, unknown>,
      userId,
      membershipByEvent.get(String((row as { id: string }).id)),
    );
    if (!shouldShowEventInWebList(event)) continue;
    byId.set(event.id, event);
  }

  return [...byId.values()].sort((a, b) => eventSortValue(b) - eventSortValue(a));
}

export async function loadEventForUser(eventId: string, userId: string): Promise<WebEvent | null> {
  const client = getSupabaseClient();
  const [{ data: eventRow, error: eventError }, { data: membership, error: membershipError }] =
    await Promise.all([
      client.from('events').select(EVENT_COLUMNS).eq('id', eventId).maybeSingle(),
      client
        .from('event_members')
        .select('event_id, role')
        .eq('event_id', eventId)
        .eq('user_id', userId)
        .maybeSingle(),
    ]);

  if (eventError) throw eventError;
  if (membershipError) throw membershipError;
  if (!eventRow) return null;

  const row = eventRow as Record<string, unknown>;
  const isOrganizer = row.organizer_id === userId;
  if (!isOrganizer && !membership) return null;

  const event = mapEventForWeb(row, userId, (membership as EventMembershipRow | null)?.role);
  return shouldShowEventInWebList(event) ? event : null;
}

export function subscribeToVisibleEventChanges(
  userId: string,
  memberEventIds: string[],
  onChange: () => void,
): () => void {
  const client = getSupabaseClient();
  let channel: RealtimeChannel = client
    .channel(`web-events-${userId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'event_members',
        filter: `user_id=eq.${userId}`,
      },
      onChange,
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'events',
        filter: `organizer_id=eq.${userId}`,
      },
      onChange,
    );

  for (const eventId of [...new Set(memberEventIds.filter(Boolean))]) {
    channel = channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'events',
        filter: `id=eq.${eventId}`,
      },
      onChange,
    );
  }

  channel = channel.subscribe();
  return () => {
    void client.removeChannel(channel);
  };
}
